// UC-ONB-014: per-hire journey customization. Reuses the canvas editor wired
// to the snapshot data sources. Doubles as the entry point for UC-ONB-011
// (start from draft) and UC-ONB-012 (discard draft).

import type { MilestoneDay, OnboardingDiscipline } from "@shared/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { useLocation, useRoute } from "wouter";
import { CanvasMilestonesPanel } from "@/components/canvas/canvas-milestones-panel";
import { useSnapshotMilestonesDataSource } from "@/components/canvas/use-snapshot-data-source";
import { WelcomeMessageEditor } from "@/components/canvas/welcome-message-editor";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { DISCIPLINE_LABELS } from "@/lib/discipline-labels";
import { orpc, rpcClient } from "@/lib/rpc";

export default function OnboardingCustomizePage() {
  const [, params] = useRoute<{ id: string }>("/onboardings/:id/customize");
  const id = params?.id ?? null;
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();

  const dashboardQuery = useQuery({
    ...orpc.onboardings.getById.queryOptions({ input: { id: id ?? "" } }),
    enabled: !!id,
  });

  const dashboard = dashboardQuery.data as any;
  const onboarding = dashboard?.onboarding;
  const isOwner =
    isSuperAdmin ||
    (dashboard?.viewer?.isFiringManager ?? false) ||
    (dashboard?.viewer?.hasOnboardingTemplateAccess ?? false) ||
    (dashboard?.viewer?.isBuddy ?? false);

  const milestonesDataSource = useSnapshotMilestonesDataSource(id ?? "", {
    isOwner,
    templateId: onboarding?.templateId ?? null,
  });

  const startFromDraft = useMutation({
    mutationFn: () => rpcClient.onboardings.startFromDraft({ id: id ?? "" }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.listActive.queryOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.listDrafts.queryOptions().queryKey,
      });
      toast({ title: "Onboarding iniciada" });
      navigate(`/onboardings/${id}`);
    },
    onError: (err: any) =>
      toast({
        title: "Erro ao iniciar",
        description: err?.message ?? "Tente novamente.",
        variant: "destructive",
      }),
  });

  const updateWelcomeMut = useMutation({
    mutationFn: (welcomeMessages: Record<string, string>) =>
      rpcClient.onboardings.updateWelcome({ id: id ?? "", welcomeMessages }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.getById.queryOptions({ input: { id: id ?? "" } }).queryKey,
      });
    },
    onError: (err: any) =>
      toast({
        title: "Não foi possível salvar a mensagem do marco",
        description: err?.message ?? "Tente novamente.",
        variant: "destructive",
      }),
  });

  const discardDraft = useMutation({
    mutationFn: () => rpcClient.onboardings.discardDraft({ id: id ?? "" }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.listDrafts.queryOptions().queryKey,
      });
      toast({ title: "Rascunho descartado" });
      navigate("/onboardings");
    },
    onError: (err: any) =>
      toast({
        title: "Erro ao descartar",
        description: err?.message ?? "Tente novamente.",
        variant: "destructive",
      }),
  });

  const isDraft = onboarding?.state === "draft";

  // Active onboardings now edit inline on the dashboard. Only drafts keep
  // this page (they have their own discard/start lifecycle).
  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate identity stable
  useEffect(() => {
    if (onboarding && !isDraft && id) {
      navigate(`/onboardings/${id}?edit=1`, { replace: true });
    }
  }, [onboarding, isDraft, id]);

  if (!id) {
    return <div className="p-6 text-sm text-destructive">ID da onboarding ausente na URL.</div>;
  }

  if (dashboardQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Carregando…</div>;
  }

  if (dashboardQuery.isError || !onboarding) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate("/onboardings")} size="sm">
          <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
        </Button>
        <p className="mt-4 text-sm text-destructive">Onboarding não encontrado.</p>
      </div>
    );
  }

  const disciplineLabel =
    DISCIPLINE_LABELS[onboarding.discipline as OnboardingDiscipline] ?? onboarding.discipline;

  if (!isDraft) {
    return null;
  }

  return (
    <div className="flex flex-col -m-6 flex-1 min-h-0">
      <div className="bg-background border-b border-border flex items-center gap-3 px-7 h-14">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(isDraft ? "/onboardings" : `/onboardings/${id}`)}
          data-testid="customize-back-btn"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {isDraft ? "Onboardings ativos" : "Dashboard"}
        </Button>
        <div className="text-sm text-muted-foreground" data-testid="customize-breadcrumb">
          Jornada de {onboarding.hireFullName} · {disciplineLabel} · {onboarding.level}
          {dashboard?.firingManager?.fullName && (
            <span className="text-muted-foreground/70">
              {" "}
              · personalizando a partir de {onboarding.template?.name ?? "template"}
            </span>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {isDraft && isOwner && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (
                    !window.confirm(
                      `Descartar rascunho de ${onboarding.hireFullName}? Esta ação não pode ser desfeita.`,
                    )
                  )
                    return;
                  discardDraft.mutate();
                }}
                disabled={discardDraft.isPending || startFromDraft.isPending}
                data-testid="customize-discard-btn"
              >
                Descartar rascunho
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (
                    !window.confirm(
                      `Iniciar onboarding de ${onboarding.hireFullName}? A jornada customizada será aplicada.`,
                    )
                  )
                    return;
                  startFromDraft.mutate();
                }}
                disabled={startFromDraft.isPending || discardDraft.isPending}
                data-testid="customize-start-btn"
              >
                {startFromDraft.isPending ? "Iniciando..." : "Iniciar onboarding"}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="border-b border-[hsl(var(--sand-divider))] bg-[hsl(var(--paper-2))] px-7 py-2 text-sm text-[hsl(var(--ink-secondary))]">
        Editando a jornada de <strong>{onboarding.hireFullName}</strong>. Mudanças afetam apenas
        este colaborador — o template original não é alterado.
      </div>

      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0 min-h-0 flex flex-col">
          <CanvasMilestonesPanel
            dataSource={milestonesDataSource}
            isOwner={isOwner}
            renderWelcomeForDay={(day: MilestoneDay) => {
              const overrides: Record<string, string> = onboarding.welcomeMessages ?? {};
              const templateMessages: Record<string, string> =
                dashboard?.templateWelcomeMessages ?? {};
              const key = String(day);
              const hasOverride = Object.hasOwn(overrides, key);
              return (
                <WelcomeMessageEditor
                  milestoneDay={day}
                  value={overrides[key] ?? ""}
                  inheritedValue={templateMessages[key] ?? ""}
                  inheriting={!hasOverride}
                  variant="override"
                  overrideBadgeLabel={onboarding.hireFullName}
                  isOwner={isOwner}
                  onCommit={(next) => {
                    const merged = { ...overrides };
                    if (next === "") delete merged[key];
                    else merged[key] = next;
                    updateWelcomeMut.mutate(merged);
                  }}
                  onRestoreOriginal={() => {
                    const merged = { ...overrides };
                    delete merged[key];
                    updateWelcomeMut.mutate(merged);
                  }}
                />
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}
